import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain, ERegistrars, IDomainRecordStatus } from '@feathr/blackbox';
import {
  Button,
  CardV2,
  CopyToClipboardButton,
  TanStackHeader,
  TanStackTable,
} from '@feathr/components';
import { DomainRecordStatusPill } from '@feathr/extender/components/DomainStatusPill';

import type { TDomainRecord } from './DomainRecords.hooks';
import { useDomainRecords, useDomainRecordStatuses } from './DomainRecords.hooks';
import { getRecordStatus, getRecordsText } from './DomainRecords.utils';

import * as styles from './DomainRecordsCard.css';

interface IProps {
  domain: Domain;
  format: ERegistrars;
}

function recordsColumns(
  t: TFunction,
  isLoadingStatuses: boolean,
  statuses: IDomainRecordStatus[],
): Array<ColumnDef<TDomainRecord>> {
  return [
    {
      accessorFn: (row): string => row.type,
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      id: 'type',
      size: 100,
    },
    {
      accessorFn: (row): string => row.name,
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Host')} />,
      id: 'host',
      meta: { isFlex: true },
    },
    {
      accessorFn: (row): string => row.value,
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Value')} />,
      id: 'value',
      meta: { isFlex: true },
    },
    {
      cell: ({ row }): JSX.Element => (
        <DomainRecordStatusPill
          isLoading={isLoadingStatuses}
          status={getRecordStatus(row.original, statuses)}
        />
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Status')} />,
      id: 'status',
      size: 120,
    },
  ];
}

function DomainRecordsCard({ domain, format }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { isLoading, records } = useDomainRecords(domain, format);
  const { isLoading: isLoadingStatuses, statuses } = useDomainRecordStatuses(domain);
  const columns = useMemo(
    () => recordsColumns(t, isLoadingStatuses, statuses),
    [t, isLoadingStatuses, statuses],
  );

  return (
    <CardV2 width={'full'}>
      <CardV2.Header title={t('View and download your domain records')}>
        <div className={styles.actions}>
          <CopyToClipboardButton
            disabled={!records.length}
            successMessage={t('Records copied to clipboard')}
            t={t}
            text={getRecordsText(records, t)}
          >
            {t('Copy records')}
          </CopyToClipboardButton>
          <Button
            href={domain.getDownloadUrl(format)}
            prefix={<FontAwesomeIcon icon={faArrowDownToLine} />}
          >
            {t('Download records')}
          </Button>
        </div>
      </CardV2.Header>
      <CardV2.Content>
        <TanStackTable<TDomainRecord>
          columns={columns}
          idKey={'value'}
          isLoading={isLoading}
          isPaginated={false}
          items={records}
        />
      </CardV2.Content>
    </CardV2>
  );
}

export default observer(DomainRecordsCard);
